import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useTranslation } from 'react-i18next'
import { Alert, Description } from './styled'
import { IPermissionsAlertConfig, IRestrictedResourcesSummary } from './types'

interface IProps {
  summary: IRestrictedResourcesSummary
  config: IPermissionsAlertConfig
}

const normalizeSpaces = (text: string) => text.replace(/\s+/g, ' ').trim()

// El catálogo remoto aún dice "perfiles" junto a etiquetas que ya lo incluyen
// ("7 perfiles perfiles", "perfiles restringidos perfiles restringidos").
const collapseRepeatedPhrase = (text: string) =>
  text.replace(/\b(\w+(?:\s+\w+)?)(?:\s+\1)+\b/gi, '$1')

const applyPlaceholders = (
  template: string,
  values: Record<string, string>
): string =>
  normalizeSpaces(
    Object.entries(values)
      .reduce((result, [key, value]) => {
        if (!value) return result
        const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        return result
          .replace(new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'g'), value)
          .replace(new RegExp(`\\{\\s*${escapedKey}\\s*\\}`, 'g'), value)
      }, template)
      .replace(/\.\.+/g, '.')
  )

export const PermissionsAlert = ({ summary, config }: IProps) => {
  const { t } = useTranslation()
  const { allowedItems, restrictedCount, total } = summary
  const allowedCount = allowedItems.length
  const alertPrefix = `${config.translationPrefix}.alert`
  const totalItemKey = config.totalItemKey ?? 'item'
  const restrictedItemKey = config.restrictedItemKey ?? 'restrictedItem'
  const totalValueKey = config.totalValueKey ?? 'totalProfiles'
  const restrictedValueKey = config.restrictedValueKey ?? 'restrictedProfiles'
  const showAdditionalMessage = config.showAdditionalMessage ?? true

  const restrictedLabel = t(`${alertPrefix}.${restrictedItemKey}`, {
    count: restrictedCount ?? 0,
  })

  const totalLabel = t(`${alertPrefix}.${totalItemKey}`, {
    count: total ?? 0,
  })

  const resourceTypeLabel = config.resourceTypeLabel ?? ''

  const placeholderValues: Record<string, string> = {
    resourceType: resourceTypeLabel,
    [totalValueKey]: totalLabel,
    [restrictedValueKey]: restrictedLabel,
    totalProfiles: totalLabel,
    restrictedProfiles: restrictedLabel,
    totalCourses: totalLabel,
    restrictedCourses: restrictedLabel,
  }

  const mainTemplateKey = allowedCount
    ? `${alertPrefix}.partialAccess`
    : `${alertPrefix}.noAccess`

  const mainMessage = collapseRepeatedPhrase(
    applyPlaceholders(t(mainTemplateKey, placeholderValues), placeholderValues)
  )

  const additionalMessage =
    showAdditionalMessage && resourceTypeLabel
      ? applyPlaceholders(
          t(`${alertPrefix}.additionalAccessMessage`, placeholderValues),
          placeholderValues
        )
      : ''

  const accessExplanation = 'solo puedes ver y editar'
  const shouldAppendAdditional =
    !!additionalMessage &&
    !normalizeSpaces(mainMessage).toLowerCase().includes(accessExplanation) &&
    !normalizeSpaces(mainMessage).includes(normalizeSpaces(additionalMessage))

  const description = shouldAppendAdditional
    ? `${mainMessage.replace(/\.$/, '')}. ${additionalMessage}`
    : mainMessage

  return (
    <Alert>
      <FontAwesomeIcon icon={faInfoCircle} />
      <Description>{description}</Description>
    </Alert>
  )
}
